/**
 * Zustand Version Control Store (AC-570)
 *
 * Manages version control state including:
 * - Project checkpoints (load, create, delete, restore)
 * - Change logs (load with filters, entity history)
 * - Undo functionality (last save tracking)
 *
 * API Contract Verified:
 * - Backend router: backend/routers/version_control.py
 * - Backend schemas: backend/schemas_version_control.py
 *
 * Related Tickets:
 * - AC-563 (Epic: Project Version Control)
 * - AC-570 (Frontend - versionStore and API service)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import apiService from '../services/api';
import {
  CheckpointSummary,
  ChangeSummary,
  ChangeFilters,
  LastSaveInfo,
  RevertToResponse,
  VersionState,
  VersionActions,
  VersionStore,
} from '../types/version_control';

// Undo timeout in milliseconds (30 seconds)
const UNDO_TIMEOUT_MS = 30000;

// Initial state
const initialState: VersionState = {
  checkpoints: [],
  checkpointsLoading: false,
  checkpointsError: null,
  changes: [],
  changesLoading: false,
  changesError: null,
  entityHistory: new Map(),
  lastSave: null,
};

/**
 * Version control store for managing project checkpoints and change history.
 */
export const useVersionStore = create<VersionStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // =======================================================================
      // CHECKPOINT ACTIONS
      // =======================================================================

      /**
       * Load checkpoints for a project.
       */
      loadCheckpoints: async (projectId: number) => {
        set({ checkpointsLoading: true, checkpointsError: null });

        try {
          const response = await apiService.getCheckpoints(projectId);
          set({
            checkpoints: response.checkpoints,
            checkpointsLoading: false,
            checkpointsError: null,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to load checkpoints';
          set({ checkpointsError: message, checkpointsLoading: false });
          throw error;
        }
      },

      /**
       * Create a new checkpoint for a project.
       */
      createCheckpoint: async (
        projectId: number,
        name: string,
        description?: string
      ): Promise<CheckpointSummary> => {
        try {
          const newCheckpoint = await apiService.createCheckpoint(projectId, {
            name,
            description,
          });

          // Prepend the new checkpoint to the list
          set((state) => ({
            checkpoints: [newCheckpoint, ...state.checkpoints],
            checkpointsError: null,
          }));

          return newCheckpoint;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Create failed';
          set({ checkpointsError: message });
          throw error;
        }
      },

      /**
       * Delete a checkpoint.
       */
      deleteCheckpoint: async (projectId: number, checkpointId: number) => {
        // Store current state for rollback
        const previousCheckpoints = get().checkpoints;

        // Optimistically remove the checkpoint
        set((state) => ({
          checkpoints: state.checkpoints.filter(
            (cp) => cp.checkpoint_id !== checkpointId
          ),
        }));

        try {
          await apiService.deleteCheckpoint(projectId, checkpointId);
        } catch (error) {
          // Rollback on failure
          set({ checkpoints: previousCheckpoints });
          throw error;
        }
      },

      /**
       * Restore a checkpoint and reload checkpoints list.
       */
      restoreCheckpoint: async (
        projectId: number,
        checkpointId: number,
        createPreRestore: boolean = true
      ) => {
        await apiService.restoreCheckpoint(projectId, checkpointId, {
          create_pre_restore_checkpoint: createPreRestore,
        });

        // Reload checkpoints to get the new pre-restore checkpoint if created
        await get().loadCheckpoints(projectId);
      },

      // =======================================================================
      // CHANGES ACTIONS
      // =======================================================================

      /**
       * Load changes for a project with optional filters.
       */
      loadChanges: async (projectId: number, filters?: ChangeFilters) => {
        set({ changesLoading: true, changesError: null });

        try {
          const response = await apiService.getChanges(projectId, filters);
          set({
            changes: response.changes,
            changesLoading: false,
            changesError: null,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to load changes';
          set({ changesError: message, changesLoading: false });
          throw error;
        }
      },

      /**
       * Load history for a specific entity.
       */
      loadEntityHistory: async (
        entityType: 'agent' | 'model' | 'orchestration' | 'queue',
        entityId: number
      ) => {
        try {
          const response = await apiService.getEntityHistory(entityType, entityId);
          const key = `${entityType}_${entityId}`;

          set((state) => {
            const newHistory = new Map(state.entityHistory);
            newHistory.set(key, response.changes);
            return { entityHistory: newHistory };
          });
        } catch (error) {
          throw error;
        }
      },

      /**
       * Re-apply a change and reload changes list.
       */
      reapplyChange: async (projectId: number, changeId: number) => {
        await apiService.reapplyChange(projectId, changeId);

        // Reload changes to get the new change created by reapply
        await get().loadChanges(projectId);
      },

      /**
       * Undo a specific change by applying its previous_state.
       * This reverts the entity to the state before this change was made.
       */
      undoChange: async (projectId: number, changeId: number) => {
        await apiService.undoChange(projectId, changeId);

        // Reload changes to get the new change created by undo
        await get().loadChanges(projectId);
      },

      /**
       * Revert an entity to the state it had at a specific change.
       *
       * This undoes all changes that occurred AFTER the specified change
       * for that entity, effectively "rewinding" the entity to that point.
       *
       * Technical Design Reference: docs/technical/TDD-AC-592-entity-revert.md
       * Ticket: AC-592
       *
       * @param projectId - Project ID the change belongs to
       * @param changeId - Change ID to revert TO (entity restored to this state)
       * @returns RevertToResponse with count and IDs of reverted changes
       */
      revertToChange: async (projectId: number, changeId: number): Promise<RevertToResponse> => {
        const result = await apiService.revertToChange(projectId, changeId);

        // Reload changes to get the new change created by revert
        await get().loadChanges(projectId);

        return result;
      },

      // =======================================================================
      // UNDO ACTIONS
      // =======================================================================

      /**
       * Set the last save info for undo functionality.
       */
      setLastSave: (save: LastSaveInfo) => {
        set({ lastSave: save });
      },

      /**
       * Clear the last save info.
       */
      clearLastSave: () => {
        set({ lastSave: null });
      },

      /**
       * Undo the last save by applying the previous_state.
       * Returns true if successful, false if no save to undo or expired.
       */
      undoLastSave: async (): Promise<boolean> => {
        const { lastSave, changes } = get();

        // No save to undo
        if (!lastSave) {
          return false;
        }

        // Check if save is expired (older than UNDO_TIMEOUT_MS)
        const now = Date.now();
        if (now - lastSave.timestamp > UNDO_TIMEOUT_MS) {
          // Clear expired save
          set({ lastSave: null });
          return false;
        }

        // Find the change to get the operation_id
        const change = changes.find((c) => c.change_id === lastSave.change_id);
        if (!change) {
          // Clear save if we can't find the change
          set({ lastSave: null });
          return false;
        }

        try {
          // Undo the change by applying previous_state
          await apiService.undoChange(change.project_id, lastSave.change_id);

          // Clear the last save after successful undo
          set({ lastSave: null });

          return true;
        } catch (error) {
          throw error;
        }
      },
    }),
    { name: 'version-store' }
  )
);

// Export selectors for optimized component subscriptions
export const selectCheckpoints = (state: VersionStore) => state.checkpoints;
export const selectCheckpointsLoading = (state: VersionStore) => state.checkpointsLoading;
export const selectCheckpointsError = (state: VersionStore) => state.checkpointsError;
export const selectChanges = (state: VersionStore) => state.changes;
export const selectChangesLoading = (state: VersionStore) => state.changesLoading;
export const selectChangesError = (state: VersionStore) => state.changesError;
export const selectEntityHistory = (state: VersionStore) => state.entityHistory;
export const selectLastSave = (state: VersionStore) => state.lastSave;

export default useVersionStore;
